import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { ProjectPermissionConstants } from "../../../../../constants";
import { updateStandardSpecificData } from "../../../../../service/project";
import { GetProjectDetailsResponse, getStandardSpecificSchemaDetails } from "../../../../../service/query";
import { ServiceError, Status } from "../../../../../service/Shared";
import { useIsLoadingWrapper } from "../../../../../utils";
import { StandardSpecificData, StandardSpecificDefinition } from "../overview/components/StandardSpecificSection/types";
import { ProjectContext } from "../ProjectContext";
import { PROJECT_DETAILS_STANDARD_SPECIFIC_SCHEMA_UUID } from "./constants";
import { UseDetailsReturnData } from "./models";
import {
  areStepKeysEqual,
  createExpressionContext,
  createStepFields,
  filterConditionalSteps,
  findDefinitionStepByKeys,
  findStepDataByPath,
  getOrderedStepKeys,
  isDefinitionStepVisible,
  mapStandardSpecificDefinitionToWizardSteps,
  normalizeSelectedStepKeys,
  setStepDataValue,
} from "./utils";

const parsePrivateStandardSpecificData = (privateStandardSpecificData: unknown): StandardSpecificData => {
  if (!privateStandardSpecificData) {
    return {};
  }

  if (typeof privateStandardSpecificData === "string") {
    try {
      return JSON.parse(privateStandardSpecificData) as StandardSpecificData;
    } catch {
      return {};
    }
  }

  return privateStandardSpecificData as StandardSpecificData;
};

export const useDetails = (): UseDetailsReturnData => {
  const { projectDetails, setProjectDetails, hasProjectPermission } = useContext(ProjectContext);
  const [standardSpecificDefinition, setStandardSpecificDefinition] = useState<StandardSpecificDefinition>();
  const [selectedStepKeys, setSelectedStepKeys] = useState<string[]>([]);
  const [isStepNavigationLoading, setIsStepNavigationLoading] = useState(false);
  const [errors, setErrors] = useState<ServiceError[] | undefined>(undefined);
  const canWriteProjectDetails = hasProjectPermission(ProjectPermissionConstants.WRITE_PROJECT_DETAILS);

  useEffect(() => {
    const fetchStandardSpecificSchemaDetails = async (): Promise<void> => {
      const response = await getStandardSpecificSchemaDetails({
        standardSpecificSchemaUuid: PROJECT_DETAILS_STANDARD_SPECIFIC_SCHEMA_UUID,
      });

      if (response.status === Status.Success && response.data) {
        setStandardSpecificDefinition(response.data.definition as StandardSpecificDefinition);
      }
    };

    fetchStandardSpecificSchemaDetails();
  }, []);

  const detailsData = useMemo(
    () => parsePrivateStandardSpecificData(projectDetails?.privateStandardSpecificData),
    [projectDetails?.privateStandardSpecificData]
  );
  const [editableStandardSpecificData, setEditableStandardSpecificData] = useState<StandardSpecificData>(detailsData);

  useEffect(() => {
    setEditableStandardSpecificData(detailsData);
  }, [detailsData]);

  const selectedProjectDetails = projectDetails as GetProjectDetailsResponse | undefined;

  const expressionContext = useMemo(
    () => createExpressionContext(editableStandardSpecificData, selectedProjectDetails),
    [editableStandardSpecificData, selectedProjectDetails]
  );

  const filteredStandardSpecificDefinition = useMemo(
    () => filterConditionalSteps(standardSpecificDefinition, expressionContext),
    [expressionContext, standardSpecificDefinition]
  );

  const steps = useMemo(
    () =>
      mapStandardSpecificDefinitionToWizardSteps({
        definition: filteredStandardSpecificDefinition,
        standardSpecificData: editableStandardSpecificData,
        selectedStepKeys,
        expressionContext,
      }),
    [editableStandardSpecificData, expressionContext, filteredStandardSpecificDefinition, selectedStepKeys]
  );

  useEffect(() => {
    setSelectedStepKeys((previousSelectedStepKeys) => {
      const normalizedSelectedStepKeys = normalizeSelectedStepKeys(steps, previousSelectedStepKeys);
      if (areStepKeysEqual(previousSelectedStepKeys, normalizedSelectedStepKeys)) {
        return previousSelectedStepKeys;
      }

      return normalizedSelectedStepKeys;
    });
  }, [steps]);

  const selectedStep = useMemo(() => {
    const maybeSelectedStep = findDefinitionStepByKeys(filteredStandardSpecificDefinition, selectedStepKeys);

    if (!isDefinitionStepVisible(maybeSelectedStep, expressionContext)) {
      return undefined;
    }

    return maybeSelectedStep;
  }, [expressionContext, filteredStandardSpecificDefinition, selectedStepKeys]);

  const selectedStepData = useMemo(
    () => findStepDataByPath(editableStandardSpecificData, selectedStepKeys),
    [editableStandardSpecificData, selectedStepKeys]
  );

  const handleFieldChange = useCallback(
    (value: unknown, key: string) => {
      setEditableStandardSpecificData((previousData) => setStepDataValue(previousData, selectedStepKeys, key, value));
    },
    [selectedStepKeys]
  );

  const stepFields = useMemo(
    () =>
      createStepFields({
        selectedStep,
        selectedDataPath: selectedStepKeys.join("."),
        selectedStepData,
        isEditable: canWriteProjectDetails,
        expressionContext,
        onFieldChange: handleFieldChange,
      }),
    [canWriteProjectDetails, expressionContext, handleFieldChange, selectedStep, selectedStepData, selectedStepKeys]
  );

  const orderedStepKeys = useMemo(() => getOrderedStepKeys(steps), [steps]);

  const selectedStepIndex = useMemo(
    () => orderedStepKeys.findIndex((stepKeys) => areStepKeysEqual(stepKeys, selectedStepKeys)),
    [orderedStepKeys, selectedStepKeys]
  );

  const hasPreviousStep = selectedStepIndex > 0;
  const hasNextStep = selectedStepIndex >= 0 && selectedStepIndex < orderedStepKeys.length - 1;

  const savePrivateStandardSpecificData = useCallback(async (): Promise<boolean> => {
    if (!canWriteProjectDetails) {
      setErrors(undefined);
      return true;
    }

    if (!projectDetails) {
      setErrors([
        {
          message: "Unable to update project details.",
        } as ServiceError,
      ]);
      return false;
    }

    const updateResponse = await updateStandardSpecificData({
      privateStandardSpecificData: editableStandardSpecificData,
      projectUuid: projectDetails.uuid,
      rowVersion: projectDetails.rowVersion,
      standardSpecificData: projectDetails.standardSpecificData ?? {},
      standardSpecificSchemaUuid: projectDetails.standardSpecificSchemaUuid ?? null,
    });

    if (updateResponse.status === Status.Success && updateResponse.data) {
      const updatedRowVersion = updateResponse.data.rowVersion;
      setErrors(undefined);

      setProjectDetails((previousProjectDetails) => {
        if (!previousProjectDetails) {
          return previousProjectDetails;
        }

        return {
          ...previousProjectDetails,
          privateStandardSpecificData: editableStandardSpecificData,
          rowVersion: updatedRowVersion,
        };
      });

      return true;
    }

    setErrors(updateResponse.errors ?? []);
    return false;
  }, [canWriteProjectDetails, editableStandardSpecificData, projectDetails, setProjectDetails]);

  const moveToStep = useCallback(
    async (targetStepKeys: string[]): Promise<void> => {
      if (areStepKeysEqual(targetStepKeys, selectedStepKeys)) {
        return;
      }

      const hasSavedData = await savePrivateStandardSpecificData();
      if (!hasSavedData) {
        return;
      }

      setSelectedStepKeys(targetStepKeys);
    },
    [savePrivateStandardSpecificData, selectedStepKeys]
  );

  const moveStepWithOffset = useCallback(
    async (stepOffset: number): Promise<void> => {
      if (selectedStepIndex < 0) {
        return;
      }

      const targetStepIndex = selectedStepIndex + stepOffset;
      if (targetStepIndex < 0 || targetStepIndex >= orderedStepKeys.length) {
        return;
      }

      await moveToStep(orderedStepKeys[targetStepIndex]);
    },
    [moveToStep, orderedStepKeys, selectedStepIndex]
  );

  const handleMoveToStepWithLoading = useIsLoadingWrapper(async (stepKeys: string[]): Promise<void> => {
    await moveToStep(stepKeys);
  }, setIsStepNavigationLoading);

  const handleMoveToStep = (stepKeys: string[]): void => {
    handleMoveToStepWithLoading(stepKeys).catch(() => {
      setErrors([
        {
          message: "Unable to move to the selected step.",
        } as ServiceError,
      ]);
    });
  };

  const handleMoveToPreviousStep = useIsLoadingWrapper(async (): Promise<void> => {
    await moveStepWithOffset(-1);
  }, setIsStepNavigationLoading);

  const handleMoveToNextStep = useIsLoadingWrapper(async (): Promise<void> => {
    await moveStepWithOffset(1);
  }, setIsStepNavigationLoading);

  return {
    steps,
    selectedStep,
    selectedStepKeys,
    hasNextStep,
    hasPreviousStep,
    isStepNavigationLoading,
    selectedDataPath: selectedStepKeys.join("."),
    standardSpecificData: editableStandardSpecificData,
    expressionContext,
    errors,
    stepFields,
    handleMoveToStep,
    handleMoveToNextStep,
    handleMoveToPreviousStep,
  };
};
